import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { AlphaBadge, BuildStamp, FreeDuringAlpha, PlayForFun } from '../components/AlphaNotice.js';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CHIP_DENOMS,
  MAX_NAME_LENGTH,
  type ChipSetId,
  type DeckId,
  type FeltId,
} from '@tv-casino/shared';
import {
  getSession,
  setPlayerName,
  signIn,
  signOut,
  signUp,
  type SessionUser,
} from '../lib/session.js';
import {
  createCasino as createCasinoFor,
  deleteCasino,
  deleteTable,
  lookupCasino,
  setCasinoPrivate,
  setCasinoTheme,
  type CasinoLookup,
} from '../lib/casinoApi.js';
import { fetchIsAdmin } from '../lib/adminApi.js';
import { chipVars, deckVars, feltVars } from '../lib/themeStyle.js';
import { DeckPreview } from '../components/DeckPreview.js';
import { BalanceChart } from '../components/BalanceChart.js';
import { MemberTable } from '../components/MemberTable.js';
import { TableTv } from '../components/TableTv.js';
import { JoinPanel } from '../components/JoinPanel.js';
import { tvGuidance } from '../components/tvLinkModel.js';
import { useTvConfig } from '../lib/useTvConfig.js';
import { applePlatform } from '../lib/platform.js';
import { casinoConsoleModel, type ConsoleView } from '@tv-casino/viewmodels/routes/casinoConsoleModel.js';
import styles from './Casino.module.css';

interface CasinoSummary {
  id: string;
  name: string;
  joinCode: string;
}

type Mode = 'sign-in' | 'sign-up';

/**
 * The owner's door. Signed out it is login/register; signed in it is the
 * venue's front desk — the tables, the two links that get everyone looking at
 * the same night, and the stats behind it. Both the casino and the screen live
 * in the URL, so a reload, a bookmark or a second tab all come back here.
 *
 * Running the table itself is deliberately not here: the owner's phone gets
 * the full host panel at the table, which is where a host is standing anyway.
 */
export function Casino() {
  const [params, setParams] = useSearchParams();
  const [session, setSession] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [casinos, setCasinos] = useState<CasinoSummary[] | null>(null);
  const [detail, setDetail] = useState<CasinoLookup | null>(null);
  const [newCasinoName, setNewCasinoName] = useState('');
  const [newTableName, setNewTableName] = useState('');
  const [mode, setMode] = useState<Mode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  /** The seat name the account plays under — reserved at its own tables. */
  const [playerName, setPlayerNameField] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Admins get a door to the site-wide dashboard in the venue bar. */
  const [admin, setAdmin] = useState(false);
  /**
   * Asked once for the whole console: LAN addresses when this tab is
   * localhost, and the Cast App ID so table-row Cast buttons can go native.
   */
  const tvConfig = useTvConfig(window.location.origin);

  /** Both halves of "where am I" ride in the query string. */
  const selectedCode = params.get('casino');
  const requestedView: ConsoleView = params.get('view') === 'stats' ? 'stats' : 'tables';

  /** One writer for the nav, so the two keys can never drift apart. */
  const navigate = useCallback(
    (next: { casino?: string | null; view?: ConsoleView }) => {
      setParams(
        (current) => {
          const p = new URLSearchParams(current);
          if (next.casino !== undefined) {
            if (next.casino) p.set('casino', next.casino);
            else p.delete('casino');
          }
          if (next.view !== undefined) {
            if (next.view === 'stats') p.set('view', 'stats');
            else p.delete('view');
          }
          return p;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const loadCasinos = useCallback(async (): Promise<CasinoSummary[]> => {
    const res = await fetch('/api/casinos', { credentials: 'include' });
    if (!res.ok) return [];
    const list = (await res.json()) as CasinoSummary[];
    setCasinos(list);
    return list;
  }, []);

  const loadDetail = useCallback(async (joinCode: string): Promise<CasinoLookup | null> => {
    const found = await lookupCasino(joinCode).catch(() => null);
    setDetail(found);
    return found;
  }, []);

  useEffect(() => {
    getSession()
      .then(async (s) => {
        setSession(s);
        setPlayerNameField(s?.playerName ?? '');
        if (s) await loadCasinos();
      })
      .catch(() => setSession(null))
      .finally(() => setLoading(false));
  }, [loadCasinos]);

  // No casino in the URL yet (a bare /casino, or the last one was deleted):
  // fall to the first one owned, and write it back so a reload lands here.
  useEffect(() => {
    if (!casinos || casinos.length === 0) return;
    const known = casinos.some((c) => c.joinCode === selectedCode);
    if (!known) navigate({ casino: casinos[0]!.joinCode, view: 'tables' });
  }, [casinos, selectedCode, navigate]);

  // The URL is the source of truth for which venue is open; the detail
  // follows it, whether it changed by a click, a back button or a reload.
  useEffect(() => {
    if (!session || !selectedCode) {
      setDetail(null);
      return;
    }
    void loadDetail(selectedCode);
  }, [session, selectedCode, loadDetail]);

  useEffect(() => {
    if (!session) {
      setAdmin(false);
      return;
    }
    void fetchIsAdmin().then(setAdmin);
  }, [session]);

  const vm = useMemo(() => casinoConsoleModel(detail, requestedView), [detail, requestedView]);

  /** Run an owner action, surface the server's refusal, re-read the venue. */
  const run = async (action: () => Promise<unknown>, after?: () => void) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      if (detail) await loadDetail(detail.joinCode);
      after?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const user =
        mode === 'sign-up'
          ? await signUp(
              email.trim(),
              password,
              name.trim() || email.trim(),
              playerName.trim() || undefined,
            )
          : await signIn(email.trim(), password);
      setSession(user);
      setPlayerNameField(user.playerName ?? '');
      await loadCasinos();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const createCasino = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await createCasinoFor(newCasinoName.trim());
      setNewCasinoName('');
      await loadCasinos();
      // Land on the casino that was just opened, not on whatever was showing.
      const code = created.joinCode;
      navigate({ casino: code, view: 'tables' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const createTable = async (e: FormEvent) => {
    e.preventDefault();
    if (!detail || !newTableName.trim()) return;
    await run(async () => {
      const res = await fetch(`/api/casinos/${detail.joinCode}/tables`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: newTableName.trim() }),
      });
      if (!res.ok) {
        throw new Error(
          ((await res.json()) as { message?: string }).message ?? 'Could not create the table.',
        );
      }
      setNewTableName('');
    });
  };

  /**
   * Claim the seat name. It is reserved at every casino this account owns —
   * a guest phone typing it is turned away, and the owner's own phone joins
   * as it without typing anything.
   */
  const savePlayerName = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const updated = await setPlayerName(playerName.trim());
      setSession(updated);
      setPlayerNameField(updated.playerName ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  /** Remove a table. Deleting the one on the TV ends that room. */
  const removeTable = (tableId: string, tableName: string) => {
    if (!detail) return;
    if (!window.confirm(`Remove ${tableName}? Anyone playing it is sent back to the lobby.`)) return;
    void run(() => deleteTable(detail.joinCode, tableId));
  };

  /** Dress every table in the venue. A TV on a table repaints as it lands. */
  const chooseFelt = (felt: FeltId) => {
    if (!detail || vm.felts.find((f) => f.active)?.id === felt) return;
    void run(() => setCasinoTheme(detail.joinCode, { felt }));
  };

  /** Swap the chip set on every rail and tray in the venue. */
  const chooseChips = (chips: ChipSetId) => {
    if (!detail || vm.chipSets.find((c) => c.active)?.id === chips) return;
    void run(() => setCasinoTheme(detail.joinCode, { chips }));
  };

  /** Deal every table in the venue from another deck. */
  const chooseDeck = (deck: DeckId) => {
    if (!detail || vm.decks.find((d) => d.active)?.id === deck) return;
    void run(() => setCasinoTheme(detail.joinCode, { deck }));
  };

  /** Hide or restore the join code and QRs on every screen of the venue. */
  const choosePrivate = (isPrivate: boolean) => {
    if (!detail || vm.isPrivate === isPrivate) return;
    void run(() => setCasinoPrivate(detail.joinCode, isPrivate));
  };

  /** Close the venue for good — tables, memberships, chips and history. */
  const closeCasino = async () => {
    if (!detail) return;
    const confirmed = window.confirm(
      `Close ${detail.name} for good? Its tables, members, chips and history are deleted. This cannot be undone.`,
    );
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await deleteCasino(detail.joinCode);
      setDetail(null);
      const list = await loadCasinos();
      navigate({ casino: list[0]?.joinCode ?? null, view: 'tables' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const leave = async () => {
    await signOut().catch(() => undefined);
    setSession(null);
    setCasinos(null);
    setDetail(null);
    setPassword('');
    navigate({ casino: null, view: 'tables' });
  };

  if (loading) {
    return (
      <main className={styles.wrap}>
        <p className={styles.hint}>Checking your session…</p>
      </main>
    );
  }

  if (!session) {
    return (
      <main className={styles.wrap}>
        <div className={styles.door}>
          <p className={styles.kicker}>Owner</p>
          <h1 className={styles.title}>
            Casino console
            <AlphaBadge />
          </h1>
          <FreeDuringAlpha />
          <p className={styles.hint}>
            Sign in to run the house. Players never need an account — they join by code or QR.
          </p>
          <form className={styles.card} onSubmit={(e) => void submit(e)}>
            {mode === 'sign-up' && (
              <>
                <label className={styles.field}>
                  Name
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Dealer Dan"
                    autoComplete="name"
                  />
                </label>
                <label className={styles.field}>
                  Player name
                  <input
                    value={playerName}
                    onChange={(e) => setPlayerNameField(e.target.value)}
                    placeholder="Dan"
                    maxLength={MAX_NAME_LENGTH}
                    autoComplete="nickname"
                  />
                  <span className={styles.hint}>
                    The name you play under. Nobody else can take it at your tables.
                  </span>
                </label>
              </>
            )}
            <label className={styles.field}>
              Email
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@party.test"
                autoComplete="email"
              />
            </label>
            <label className={styles.field}>
              Password
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete={mode === 'sign-up' ? 'new-password' : 'current-password'}
              />
            </label>
            {error && <p className={styles.error}>{error}</p>}
            <button className={`${styles.button} ${styles.primary}`} disabled={busy}>
              {busy ? 'One moment…' : mode === 'sign-up' ? 'Create account' : 'Sign in'}
            </button>
            <button
              type="button"
              className={styles.linkish}
              onClick={() => {
                setMode(mode === 'sign-up' ? 'sign-in' : 'sign-up');
                setError(null);
              }}
            >
              {mode === 'sign-up' ? 'I already have an account' : 'Need an account? Register'}
            </button>
          </form>
          <Link className={styles.linkish} to="/">
            Back to the party
          </Link>
          <PlayForFun />
        </div>
      </main>
    );
  }

  /** The venue's own header: who is open, where the night is, and the code. */
  const header = detail && (
    <header className={styles.bar}>
      <div className={styles.barTitle}>
        <p className={styles.kicker}>Owner console</p>
        <h1 className={styles.venue}>
          {vm.casinoName}
          <AlphaBadge />
        </h1>
        <p className={styles.status}>{vm.nightStatus}</p>
      </div>
      <div className={styles.barSide}>
        {casinos && casinos.length > 1 && (
          <label className={styles.picker}>
            <span className={styles.pickerLabel}>Casino</span>
            <select
              value={selectedCode ?? ''}
              onChange={(e) => navigate({ casino: e.target.value, view: 'tables' })}
            >
              {casinos.map((c) => (
                <option key={c.id} value={c.joinCode}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {admin && (
          <Link className={styles.linkish} to="/admin">
            Admin
          </Link>
        )}
        <Link
          className={styles.linkish}
          to={selectedCode ? `/profile?casino=${selectedCode}` : '/profile'}
        >
          Profile
        </Link>
        <button type="button" className={styles.linkish} onClick={() => void leave()}>
          Sign out
        </button>
      </div>
    </header>
  );

  return (
    <main className={styles.wrap}>
      {header}

      {detail && (
        <nav className={styles.tabs} aria-label="Casino navigation">
          {vm.tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={tab.active ? styles.tabOn : styles.tab}
              aria-current={tab.active ? 'page' : undefined}
              onClick={() => navigate({ view: tab.id })}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      )}

      {error && <p className={styles.error}>{error}</p>}

      {casinos && casinos.length === 0 && (
        <form className={styles.card} onSubmit={(e) => void createCasino(e)}>
          <p className={styles.hint}>No casinos yet — open your first one.</p>
          <label className={styles.field}>
            New casino
            <input
              value={newCasinoName}
              onChange={(e) => setNewCasinoName(e.target.value)}
              placeholder="The Velvet Room"
              required
            />
          </label>
          <button
            className={`${styles.button} ${styles.primary}`}
            disabled={busy || !newCasinoName.trim()}
          >
            Open a casino
          </button>
        </form>
      )}

      {detail && vm.view === 'tables' && (
        <div className={styles.grid}>
          <section className={styles.card} aria-label="Tables">
            <p className={styles.cardHead}>Tables</p>
            {vm.nextStep && <p className={styles.step}>{vm.nextStep}</p>}
            {vm.emptyTablesLine ? (
              <p className={styles.hint}>{vm.emptyTablesLine}</p>
            ) : (
              <ul className={styles.tables}>
                {vm.tables.map((table) => (
                  <li
                    key={table.id}
                    className={[
                      styles.tableRow,
                      table.live ? styles.rowOn : '',
                      table.closed ? styles.closed : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <div className={styles.rowHead}>
                      <span className={styles.tableName}>
                        <strong>{table.name}</strong>
                        <span className={styles.game}>{table.gameLabel}</span>
                      </span>
                      <span className={styles.rowMeta}>
                        {table.actionLabel && (
                          <span className={styles.tableCode}>{table.actionLabel}</span>
                        )}
                        <span className={table.live ? styles.badgeOn : styles.badge}>
                          {table.note}
                        </span>
                      </span>
                    </div>
                    <div className={styles.rowFoot}>
                      {table.tvUrl && <TableTv tvPath={table.tvUrl} config={tvConfig} />}
                      <button
                        type="button"
                        className={`${styles.small} ${styles.danger} ${styles.remove}`}
                        disabled={busy}
                        aria-label={`Remove ${table.name}`}
                        onClick={() => removeTable(table.id, table.name)}
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {vm.tables.some((t) => t.tvUrl) && (
              <p className={styles.hint}>{tvGuidance(applePlatform())}</p>
            )}

            <details className={styles.more}>
              <summary>Add a table</summary>
              <form className={styles.addTable} onSubmit={(e) => void createTable(e)}>
                <input
                  value={newTableName}
                  onChange={(e) => setNewTableName(e.target.value)}
                  placeholder="New table name"
                  required
                />
                <button
                  className={`${styles.small} ${styles.primary}`}
                  disabled={busy || !newTableName.trim()}
                >
                  Add table
                </button>
              </form>
            </details>
          </section>

          <div className={styles.side}>
            <JoinPanel playPath={vm.playUrl} joinCode={vm.joinCode} config={tvConfig} />

            <details className={styles.more}>
              <summary>Table felt</summary>
              <div className={styles.addTable}>
                <p className={styles.hint}>
                  The cloth on every table here, on the TV and on the phones sitting at it.
                </p>
                <div className={styles.swatches} role="group" aria-label="Table felt">
                  {vm.felts.map((felt) => (
                    <button
                      key={felt.id}
                      type="button"
                      className={felt.active ? styles.swatchOn : styles.swatch}
                      style={feltVars(felt.id)}
                      aria-pressed={felt.active}
                      disabled={busy}
                      onClick={() => chooseFelt(felt.id)}
                    >
                      {felt.label}
                    </button>
                  ))}
                </div>
              </div>
            </details>
            <details className={styles.more}>
              <summary>Chips</summary>
              <div className={styles.addTable}>
                <p className={styles.hint}>
                  The chips on every phone's rail and tray here. Chips already on the felt keep
                  their player's colour.
                </p>
                <div className={styles.swatches} role="group" aria-label="Chip set">
                  {vm.chipSets.map((set) => (
                    <button
                      key={set.id}
                      type="button"
                      className={`${set.active ? styles.swatchOn : styles.swatch} ${styles.chipSwatch}`}
                      style={chipVars(set.id)}
                      aria-pressed={set.active}
                      disabled={busy}
                      onClick={() => chooseChips(set.id)}
                    >
                      <span className={styles.chipRow} aria-hidden="true">
                        {CHIP_DENOMS.map((denom) => (
                          <span
                            key={denom}
                            className={styles.miniChip}
                            style={{
                              background: `radial-gradient(circle at 50% 35%, var(--chip${denom}-top), var(--chip${denom}-rim) 70%)`,
                              borderColor: `var(--chip${denom}-edge)`,
                            }}
                          />
                        ))}
                      </span>
                      {set.label}
                    </button>
                  ))}
                </div>
              </div>
            </details>
            <details className={styles.more}>
              <summary>Cards</summary>
              <div className={styles.addTable}>
                <p className={styles.hint}>
                  The cards dealt at every table here: the backs on the TV and the faces in every
                  hand.
                </p>
                <div className={styles.swatches} role="group" aria-label="Card deck">
                  {vm.decks.map((deck) => (
                    <button
                      key={deck.id}
                      type="button"
                      className={`${deck.active ? styles.swatchOn : styles.swatch} ${styles.deckSwatch}`}
                      style={deckVars(deck.id)}
                      aria-pressed={deck.active}
                      disabled={busy}
                      onClick={() => chooseDeck(deck.id)}
                    >
                      <DeckPreview />
                      {deck.label}
                    </button>
                  ))}
                </div>
              </div>
            </details>

            <details className={styles.more}>
              <summary>Privacy</summary>
              <div className={styles.addTable}>
                <label className={styles.picker}>
                  <input
                    type="checkbox"
                    checked={vm.isPrivate}
                    disabled={busy}
                    onChange={(e) => choosePrivate(e.target.checked)}
                  />
                  <span className={styles.pickerLabel}>Private venue</span>
                </label>
                <p className={styles.hint}>
                  {vm.isPrivate
                    ? 'The join code and QRs are hidden from every TV and phone. Share the link above privately — anyone who has it can still walk in.'
                    : 'Turn on to hide the join code and QRs from every TV and phone. You keep the link above for inviting people.'}
                </p>
              </div>
            </details>

            <details className={styles.more}>
              <summary>Open another casino</summary>
              <form className={styles.addTable} onSubmit={(e) => void createCasino(e)}>
                <input
                  value={newCasinoName}
                  onChange={(e) => setNewCasinoName(e.target.value)}
                  placeholder="The Back Room"
                  required
                />
                <button className={styles.small} disabled={busy || !newCasinoName.trim()}>
                  Open a casino
                </button>
              </form>
            </details>

            <details className={styles.more}>
              <summary>Player name</summary>
              <form className={styles.addTable} onSubmit={(e) => void savePlayerName(e)}>
                <p className={styles.hint}>
                  The name you play under. It is held for you at every table here — a guest
                  typing it is turned away, and your phone sits down as it.
                </p>
                <input
                  value={playerName}
                  onChange={(e) => setPlayerNameField(e.target.value)}
                  placeholder="Dan"
                  maxLength={MAX_NAME_LENGTH}
                  aria-label="Player name"
                />
                <button className={styles.small} disabled={busy}>
                  Save player name
                </button>
              </form>
            </details>

            <details className={styles.more}>
              <summary>Close this casino</summary>
              <p className={styles.hint}>
                Deletes {vm.casinoName} along with its tables, members, chips and history.
                There is no undo.
              </p>
              <button
                type="button"
                className={`${styles.small} ${styles.danger}`}
                disabled={busy}
                onClick={() => void closeCasino()}
              >
                Close {vm.casinoName} for good
              </button>
            </details>
          </div>
        </div>
      )}

      {detail && vm.view === 'stats' && (
        <div className={styles.stats}>
          <BalanceChart casinoCode={detail.joinCode} />
          <MemberTable casinoCode={detail.joinCode} />
        </div>
      )}

      <Link className={styles.linkish} to="/">
        Back to the party
      </Link>
      <BuildStamp />
    </main>
  );
}
